import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Facebook, 
  Instagram, 
  CheckCircle2, 
  XCircle,
  AlertCircle,
  ChevronRight,
  Info,
  ShieldCheck,
  Zap,
  Share2,
  Trash2,
  ArrowLeft,
  Activity,
  Settings
} from 'lucide-react';
import api from '../../../services/api';

export default function SocialMediaConnect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [toast, setToast] = useState(null);
  const [expandedPermission, setExpandedPermission] = useState(false);
  const [updatingCreds, setUpdatingCreds] = useState(false);
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [configId, setConfigId] = useState('');

  useEffect(() => {
    fetchAccounts();
    fetchCredentials();
    
    // Check for OAuth callback
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    
    if (code) {
      handleOAuthCallback(code, state);
    }
  }, [location]);

  const fetchCredentials = async () => {
    try {
      const res = await api.get('/api/social-media/credentials');
      setAppId(res.data?.meta_app_id || '');
      setAppSecret(res.data?.meta_app_secret || '');
      setConfigId(res.data?.meta_config_id || '');
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
    }
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    try {
      setUpdatingCreds(true);
      await api.put('/api/social-media/credentials', {
        meta_app_id: appId,
        meta_app_secret: appSecret,
        meta_config_id: configId
      });
      showToast('success', 'Meta Developer credentials updated successfully!');
      fetchCredentials();
    } catch (error) {
      console.error('Update credentials error:', error);
      showToast('error', 'Failed to update Meta credentials');
    } finally {
      setUpdatingCreds(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/social-media/accounts');
      setAccounts(res.data || []);
    } catch (error) {
      console.error('Fetch accounts error:', error);
      showToast('error', 'Failed to fetch connected accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthCallback = async (code, state) => {
    try {
      showToast('info', 'Connecting account...');
      await api.get(`/api/social-media/callback?code=${code}&state=${state}`);
      showToast('success', 'Account connected successfully!');
      // Remove query params
      navigate(location.pathname, { replace: true });
      fetchAccounts();
    } catch (error) {
      console.error('OAuth callback error:', error);
      showToast('error', 'Failed to connect account. Please try again.');
      navigate(location.pathname, { replace: true });
    }
  };

  const [disconnectTarget, setDisconnectTarget] = useState(null); // { id, platform }

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const handleConnect = async (platform) => {
    try {
      showToast('info', `Redirecting to ${platform} for authentication...`);
      const res = await api.get(`/api/social-media/connect?platform=${platform}`);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('Connection URL not returned from backend');
      }
    } catch (error) {
      console.error('Connect error:', error);
      showToast('error', `Failed to initiate ${platform} connection`);
    }
  };

  const handleDisconnectClick = (id, platform) => {
    setDisconnectTarget({ id, platform });
  };

  const executeDisconnect = async () => {
    if (!disconnectTarget) return;
    const { id, platform } = disconnectTarget;
    setDisconnectTarget(null);
    try {
      await api.delete(`/api/social-media/accounts/${id}`);
      setAccounts(prev => prev.filter(acc => acc.id !== id));
      showToast('success', 'Account disconnected successfully');
    } catch (error) {
      console.error('Disconnect error:', error);
      showToast('error', 'Failed to disconnect account');
    }
  };

  const [showManualConnect, setShowManualConnect] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [connectingManual, setConnectingManual] = useState(false);

  const handleManualConnect = async (e) => {
    e.preventDefault();
    if (!manualToken.trim()) {
      showToast('error', 'Please enter an access token');
      return;
    }

    try {
      setConnectingManual(true);
      const res = await api.post('/api/social-media/manual-connect', { access_token: manualToken.trim() });
      showToast('success', res.data.message || 'Account connected successfully!');
      setShowManualConnect(false);
      setManualToken('');
      fetchAccounts();
    } catch (error) {
      console.error('Manual connect error:', error);
      showToast('error', error.response?.data?.error || 'Failed to connect account manually');
    } finally {
      setConnectingManual(false);
    }
  };

  const getAccount = (platform) => accounts.find(acc => acc.platform === platform);
  const fbAccount = getAccount('facebook');
  const igAccount = getAccount('instagram');

  return (
    <div className="min-h-screen bg-gray-50/50 p-8 font-sans pb-24">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-8 right-8 z-50 p-4 rounded-xl shadow-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-5 max-w-md ${
          toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 size={20} /> : 
           toast.type === 'error' ? <XCircle size={20} /> : 
           <Info size={20} />}
          <p className="font-medium text-sm">{toast.message}</p>
        </div>
      )}

      {/* Header */}
      <div className="mb-10">
        <button 
          onClick={() => navigate('/admin/social-media')}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 mb-4 transition-colors w-fit"
        >
          <ArrowLeft size={16} className="mr-1" /> Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Connect Accounts</h1>
        <p className="text-gray-500 mt-2 max-w-2xl">Connect your restaurant's social media profiles to start publishing posts, tracking engagement, and managing comments from one central dashboard.</p>
      </div>

      {/* Meta Developer Credentials Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Settings size={20} className="text-primary-600" />
          Meta App Credentials
        </h2>
        <p className="text-sm text-gray-500 mb-6">Configure your own Facebook Developer App ID and App Secret keys so that your restaurant's custom domain (e.g. savorybites.aaharamcloud.com) handles social accounts connection securely.</p>
        <form onSubmit={handleUpdateCredentials} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Meta App ID</label>
            <input 
              type="text" 
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="e.g. 1575115294394799"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50/50"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Meta App Secret</label>
            <input 
              type="password" 
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50/50"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Meta Login Config ID (Optional)</label>
            <input 
              type="text" 
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              placeholder="e.g. 12210576721541"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50/50"
            />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button 
              type="submit" 
              disabled={updatingCreds}
              className="py-2.5 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium text-sm rounded-xl hover:shadow-md transition-all disabled:opacity-50"
            >
              {updatingCreds ? 'Saving...' : 'Save Credentials'}
            </button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
          <div className="h-64 bg-gray-200 rounded-2xl"></div>
          <div className="h-64 bg-gray-200 rounded-2xl"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Facebook Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-shadow relative">
            <div className="h-2 w-full bg-gradient-to-r from-blue-500 to-blue-700"></div>
            <div className="p-8 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                    <Facebook size={28} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Facebook Page</h2>
                    <p className="text-sm text-gray-500">Reach your local community</p>
                  </div>
                </div>
                {fbAccount && (
                  <span className="flex items-center gap-1.5 text-xs font-semibold bg-green-100 text-green-800 px-3 py-1 rounded-full border border-green-200 shadow-sm">
                    <CheckCircle2 size={14} /> Connected
                  </span>
                )}
              </div>

              {fbAccount ? (
                <div className="mt-auto bg-gray-50 p-5 rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <img src={fbAccount.profile_picture_url} alt="Profile" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
                      <div>
                        <h3 className="font-semibold text-gray-900">{fbAccount.account_name}</h3>
                        <p className="text-xs text-gray-500">{fbAccount.followers || 0} followers</p>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleDisconnectClick(fbAccount.id, 'facebook')}
                    className="w-full py-2.5 px-4 bg-white border border-red-200 text-red-600 font-medium text-sm rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 size={16} /> Disconnect
                  </button>
                </div>
              ) : (
                <div className="mt-auto flex flex-col">
                  <ul className="space-y-3 mb-8">
                    {['Publish directly to your Page', 'Reply to comments from the dashboard', 'Track page reach and engagement'].map((item, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-600">
                        <CheckCircle2 size={18} className="text-blue-500 mr-2 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={() => handleConnect('facebook')}
                    className="w-full py-3.5 px-4 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 group"
                  >
                    Connect Facebook
                    <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button 
                    onClick={() => setShowManualConnect(true)}
                    className="w-full mt-3 py-2 px-4 bg-gray-50 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center gap-2 text-sm"
                  >
                    Manual Token Connect
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Instagram Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-shadow relative">
            <div className="h-2 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"></div>
            <div className="p-8 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center text-pink-600">
                    <Instagram size={28} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Instagram Business</h2>
                    <p className="text-sm text-gray-500">Visual storytelling & engagement</p>
                  </div>
                </div>
                {igAccount && (
                  <span className="flex items-center gap-1.5 text-xs font-semibold bg-green-100 text-green-800 px-3 py-1 rounded-full border border-green-200 shadow-sm">
                    <CheckCircle2 size={14} /> Connected
                  </span>
                )}
              </div>

              {igAccount ? (
                <div className="mt-auto bg-gray-50 p-5 rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <img src={igAccount.profile_picture_url} alt="Profile" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
                      <div>
                        <h3 className="font-semibold text-gray-900">{igAccount.account_name}</h3>
                        <p className="text-xs text-gray-500">{igAccount.followers || 0} followers</p>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleDisconnectClick(igAccount.id, 'instagram')}
                    className="w-full py-2.5 px-4 bg-white border border-red-200 text-red-600 font-medium text-sm rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 size={16} /> Disconnect
                  </button>
                </div>
              ) : (
                <div className="mt-auto flex flex-col">
                  {!fbAccount && (
                    <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2 text-orange-800 text-xs">
                      <AlertCircle size={16} className="shrink-0 mt-0.5" />
                      <p>You must connect a Facebook Page first. Instagram Business accounts must be linked to a Facebook Page.</p>
                    </div>
                  )}
                  <ul className="space-y-3 mb-8">
                    {['Post photos and videos directly', 'Manage Instagram comments', 'View audience demographics'].map((item, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-600">
                        <CheckCircle2 size={18} className="text-pink-500 mr-2 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={() => handleConnect('instagram')}
                    disabled={!fbAccount}
                    className={`w-full py-3.5 px-4 font-medium rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 group ${
                      !fbAccount 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 hover:shadow-md'
                    }`}
                  >
                    Connect Instagram
                    <ChevronRight size={18} className={fbAccount ? "group-hover:translate-x-1 transition-transform" : ""} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* How It Works Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-64 h-64 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full blur-3xl opacity-50"></div>
        <h2 className="text-2xl font-bold text-gray-900 mb-8 relative z-10">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
          {[
            { icon: Zap, title: "1. Connect", desc: "Link your accounts securely. We never store your passwords." },
            { icon: Share2, title: "2. Post", desc: "Create and schedule posts to multiple platforms at once." },
            { icon: Activity, title: "3. Grow", desc: "Engage with customers and track your performance." }
          ].map((step, i) => (
            <div key={i} className="text-center group">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:-translate-y-1 transition-transform duration-300">
                <step.icon size={32} />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Permissions Accordion */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <button 
          onClick={() => setExpandedPermission(!expandedPermission)}
          className="w-full flex items-center justify-between p-6 bg-gray-50/50 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-gray-600" size={24} />
            <div className="text-left">
              <h3 className="font-bold text-gray-900">Privacy & Permissions</h3>
              <p className="text-xs text-gray-500 font-normal mt-0.5">Learn what data we access</p>
            </div>
          </div>
          <ChevronRight size={20} className={`text-gray-400 transition-transform duration-300 ${expandedPermission ? 'rotate-90' : ''}`} />
        </button>
        
        <div className={`transition-all duration-300 ease-in-out ${expandedPermission ? 'max-h-[500px] opacity-100 border-t border-gray-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600 mb-4">We request only the permissions necessary to provide you with social media management features.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><CheckCircle2 size={16} className="text-green-500"/> What we can do</h4>
                <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
                  <li>Publish posts on your behalf</li>
                  <li>Read and reply to comments</li>
                  <li>View page insights and metrics</li>
                </ul>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><XCircle size={16} className="text-red-500"/> What we CANNOT do</h4>
                <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
                  <li>Access your personal profile</li>
                  <li>Change your account password</li>
                  <li>Send private messages</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Confirm Disconnect Modal */}
      {disconnectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop Overlay */}
          <div 
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setDisconnectTarget(null)}
          ></div>

          {/* Modal Container */}
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 p-6 relative z-10 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3.5 mb-4 text-amber-600">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle size={24} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Disconnect {disconnectTarget.platform === 'facebook' ? 'Facebook' : 'Instagram'}?</h3>
                <p className="text-xs text-gray-500">This action can be undone at any time.</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              Are you sure you want to disconnect this account? You will no longer be able to publish posts or sync comments and analytics for this platform from your dashboard.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDisconnectTarget(null)}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeDisconnect}
                className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-rose-600 hover:shadow-lg hover:shadow-rose-100 active:scale-95 rounded-xl transition-all"
              >
                Disconnect Account
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Manual Token Connect Modal */}
      {showManualConnect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop Overlay */}
          <div 
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => !connectingManual && setShowManualConnect(false)}
          ></div>

          {/* Modal Container */}
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-100 p-6 relative z-10 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 text-blue-600">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">Manual Token Connect</h3>
                  <p className="text-xs text-gray-500">Bypass OAuth for New Pages Experience</p>
                </div>
              </div>
              <button 
                onClick={() => !connectingManual && setShowManualConnect(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={connectingManual}
              >
                <XCircle size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              If the standard Connect button fails (especially for New Pages Experience accounts), you can manually connect by generating a token using the <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Graph API Explorer</a>. Make sure to grant all necessary permissions.
            </p>

            <form onSubmit={handleManualConnect}>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Access Token</label>
                <textarea 
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="Paste your long-lived or short-lived Facebook access token here..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50/50 resize-none font-mono"
                  disabled={connectingManual}
                  required
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowManualConnect(false)}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-all disabled:opacity-50"
                  disabled={connectingManual}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={connectingManual || !manualToken.trim()}
                  className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg hover:shadow-blue-100 active:scale-95 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {connectingManual ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect Account'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
